/**
 * Reddit Advertising OAuth Utilities (Story 19.10, Task 2)
 *
 * Handles OAuth 2.0 + PKCE URL building, code exchange, refresh, and error
 * parsing against the Reddit OAuth 2.0 endpoints
 * (`https://www.reddit.com/api/v1/authorize` for authorize,
 *  `https://www.reddit.com/api/v1/access_token` for token).
 *
 * Reddit characteristics that distinguish it from every other OAuth integration
 * in this codebase:
 *  - **HTTP Basic Authentication** for the token endpoint (matches Pinterest,
 *    diverges from Snapchat / Microsoft Ads / Klaviyo which use form-body
 *    credentials). `client_id` is the username and `client_secret` is the
 *    password (RFC 7617).
 *  - **`User-Agent` header REQUIRED** on every API call. Reddit explicitly
 *    throttles or rejects requests with default user-agent strings. Reddit also
 *    treats `User-Agent` as the per-app rate-limit key — sharing it across
 *    multiple apps would pool quota. We env-suffix it (production / staging) so
 *    deploys are differentiated as separate Reddit rate-quota buckets.
 *  - **`duration=permanent` MANDATORY** on `/authorize` — without it Reddit
 *    returns a 1-hour access token with NO refresh_token (lock-in re-auth-every-
 *    hour death spiral).
 *  - **Refresh tokens DO NOT rotate** — Reddit's docs state "refresh_token will
 *    only be present if one was requested" and it is only requested on the
 *    INITIAL `/authorize`, not on subsequent refreshes. The refresh response
 *    omits the `refresh_token` field entirely. The adapter MUST carry forward
 *    the existing refresh_token unchanged. **This INVERTS the Snapchat 19.7
 *    defensive-rotation pattern** (Snap always returns it, Reddit never does).
 *  - **Aggressive 60-minute access token TTL** (matches Klaviyo / MS Ads / Snap).
 *  - PKCE (code_challenge_method=S256) sent for defense-in-depth and uniformity
 *    with Klaviyo + Microsoft Ads + Pinterest + Snapchat. Reddit's confidential-
 *    client flow does not require PKCE but tolerates extra params.
 *  - `redirect_uri` exact match — even trailing-slash differences fail with
 *    `invalid_grant`.
 *  - Scopes: `adsread` (no colon — verified vs OpenAPI; the integration-mapping
 *    directive's `ads:read` notation is wrong). We request only `adsread`
 *    because OAuth-only routes never write campaigns from ProvenLeap.
 *  - Scope validation: if Reddit silently narrows scope (returns `""` or omits
 *    `adsread`), the adapter fails loud with `scope-narrowed` and tokens are
 *    NOT persisted (silent scope-narrowing is a known OAuth attack vector).
 *
 * @see https://github.com/reddit-archive/reddit/wiki/OAuth2
 * @see https://business.reddithelp.com/s/article/authenticate-your-developer-application
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const REDDIT_AUTHORIZE_URL = "https://www.reddit.com/api/v1/authorize";
const REDDIT_TOKEN_URL = "https://www.reddit.com/api/v1/access_token";
/**
 * Reddit's revoke endpoint — used by adapter `disconnect()` if/when Story 19.11
 * wires it. Not used in 19.10 but exported here so the constant is co-located
 * with the other Reddit OAuth endpoints. HTTP Basic Auth + User-Agent header
 * are required (same auth shape as the token endpoint).
 *
 * @see https://github.com/reddit-archive/reddit/wiki/OAuth2#manually-revoking-a-token
 */
export const REDDIT_REVOKE_URL = "https://www.reddit.com/api/v1/revoke_token";
const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

/**
 * Scopes requested for Reddit Advertising integration.
 *
 * `adsread` grants read-only access to the Reddit Ads API (campaigns, ads,
 * reports). `adsedit` and `adsconversions` are NOT requested in 19-10 — we
 * never write campaigns or post conversions from ProvenLeap.
 *
 * **NOTE:** Reddit's actual scope strings are `adsread` / `adsedit` /
 * `adsconversions` — **NO COLON** (verified against the official OpenAPI spec
 * at `https://ads-api.reddit.com/api/v3/openapi.json`).
 */
export const REDDIT_ADS_SCOPES = ["adsread"] as const;

/**
 * Builds the canonical Reddit `User-Agent` header value, env-suffixed so
 * production and staging deploys are differentiated as separate Reddit rate-
 * quota buckets (Round 2 SRE Pre-mortem Failure A — UA-reuse pooling footgun).
 *
 * Reddit treats the User-Agent as the per-app rate-limit key. All callers
 * (oauth.ts, api.ts, queue refresh handlers) MUST construct the same identifier
 * via this helper; never inline the literal string.
 */
export function getRedditUserAgent(env: {
  WORKER_ENV?: string;
  ENVIRONMENT?: string;
}): string {
  const tier = env.WORKER_ENV ?? env.ENVIRONMENT ?? "unknown";
  return `ProvenLeap/1.0 (${tier}; https://provenleap.com)`;
}

// ============================================================================
// Types
// ============================================================================

export interface RedditAdsAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** PKCE code_challenge (Base64URL of SHA-256 of code_verifier) */
  codeChallenge: string;
}

export interface RedditAdsTokenResponse {
  access_token: string;
  /** Reddit only includes refresh_token on the INITIAL exchange (with `duration=permanent`). Refresh responses OMIT this field. */
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface RedditAdsCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Build Authorization URL
// ============================================================================

/**
 * Builds the Reddit OAuth 2.0 + PKCE authorization URL.
 *
 *   - Authorize URL is `https://www.reddit.com/api/v1/authorize`
 *   - `scope` is space-joined per OAuth 2.0 spec (single value `adsread` here)
 *   - `duration=permanent` is MANDATORY — without it Reddit returns a 1-hour
 *     access token with NO refresh_token. **lowercase string literal**, NOT
 *     `Permanent` / `PERMANENT` (Round 2 Pre-mortem Failure E case-sensitivity
 *     guard).
 *   - `code_challenge_method=S256` for defense-in-depth (Reddit tolerates PKCE
 *     on confidential clients).
 */
export function buildRedditAdsAuthUrl(
  options: RedditAdsAuthUrlOptions
): string {
  const { clientId, redirectUri, stateKey, codeChallenge } = options;

  if (!clientId) {
    throw new Error("REDDIT_ADS_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("REDDIT_ADS_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }
  if (!codeChallenge) {
    throw new Error("codeChallenge is required for PKCE");
  }

  const url = new URL(REDDIT_AUTHORIZE_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("scope", REDDIT_ADS_SCOPES.join(" "));
  url.searchParams.set("duration", "permanent");
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", codeChallenge);

  return url.toString();
}

// ============================================================================
// Typed token-endpoint error (so adapter callers don't regex-match messages)
// ============================================================================

export interface RedditAdsTokenErrorOptions {
  /** The verbatim value of the `error` field from Reddit's JSON envelope */
  oauthError?: string;
}

export class RedditAdsTokenError extends Error {
  readonly oauthError: string | undefined;
  constructor(message: string, options: RedditAdsTokenErrorOptions = {}) {
    super(message);
    this.name = "RedditAdsTokenError";
    this.oauthError = options.oauthError;
  }
}

/**
 * Pulls the OAuth `error` code out of a sanitized provider body.
 *
 * @internal exported for direct unit testing only
 */
export function extractOAuthErrorCode(
  providerBody: string | undefined
): string | undefined {
  if (!providerBody) return undefined;
  try {
    const parsed: unknown = JSON.parse(providerBody);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const err = (parsed as Record<string, unknown>).error;
    if (typeof err === "string" && err.length > 0) {
      return err;
    }
    if (err !== null && typeof err === "object") {
      const nestedCode = (err as Record<string, unknown>).code;
      if (typeof nestedCode === "string" && nestedCode.length > 0) {
        return nestedCode;
      }
      const nestedError = (err as Record<string, unknown>).error;
      if (typeof nestedError === "string" && nestedError.length > 0) {
        return nestedError;
      }
    }
  } catch {
    // Not JSON — return undefined so callers fall back to the message body.
  }
  return undefined;
}

/**
 * Pulls the most descriptive message out of a Reddit token error envelope,
 * with empty-string fallback when nothing structured is present.
 *
 * Reddit's token-endpoint errors come back as the standard OAuth 2.0 form:
 *   `{ "error": "invalid_grant", "error_description": "..." }`
 * or the simpler legacy form:
 *   `{ "error": "..." }` / `{ "message": "..." }`
 */
export function extractOAuthErrorDescription(providerBody: string): string {
  try {
    const parsed = JSON.parse(providerBody) as {
      error?: unknown;
      error_description?: unknown;
      message?: unknown;
    };
    const err =
      typeof parsed.error === "string" && parsed.error.length > 0
        ? parsed.error
        : "";
    const desc =
      typeof parsed.error_description === "string" &&
      parsed.error_description.length > 0
        ? parsed.error_description
        : "";
    const msg =
      typeof parsed.message === "string" && parsed.message.length > 0
        ? parsed.message
        : "";

    if (err && desc) return `${err}: ${desc}`;
    if (desc) return desc;
    if (err) return err;
    if (msg) return msg;
    return "";
  } catch {
    return "";
  }
}

/**
 * Builds the HTTP Basic Authorization header value for Reddit's token endpoint.
 *
 * Reddit uses **HTTP Basic Auth** for `/api/v1/access_token` and
 * `/api/v1/revoke_token`. `client_id` is the username, `client_secret` is the
 * password. Both are URL-form-encoded into the basic-auth credential pair per
 * RFC 7617.
 *
 * @internal exported for direct unit testing only
 */
export function buildBasicAuthHeader(
  clientId: string,
  clientSecret: string
): string {
  // btoa is a Cloudflare Workers global; it operates on Latin-1 strings.
  // client_id / client_secret from Reddit are ASCII-only, so this is safe.
  return `Basic ${btoa(`${clientId}:${clientSecret}`)}`;
}

// ============================================================================
// Token Exchange
// ============================================================================

/**
 * Exchanges an authorization code for access + refresh tokens.
 *
 * CRITICAL: Reddit uses HTTP Basic Authentication for the token endpoint.
 * `client_id` and `client_secret` go in the `Authorization: Basic ...` header,
 * NOT the body. This matches Pinterest, **diverges from Snapchat / Microsoft
 * Ads / Klaviyo which use form-body credentials**. The body contains
 * `grant_type=authorization_code`, `code`, `redirect_uri`, and `code_verifier`.
 *
 * The `User-Agent` header is REQUIRED — Reddit explicitly throttles or rejects
 * requests with default user-agent strings.
 *
 * The returned token response is validated:
 *   1. `access_token` is a non-empty string
 *   2. `refresh_token` is a non-empty string (initial exchange only — Reddit
 *      issues it because we sent `duration=permanent`)
 *   3. `expires_in` is a finite number (NaN-prevention for arithmetic)
 *   4. `scope` includes `adsread` — silent scope-narrowing is rejected loudly
 *      with a `scope-narrowed` error (AC #2 invariant).
 */
export async function exchangeRedditAdsCode(
  code: string,
  codeVerifier: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string,
  userAgent: string
): Promise<RedditAdsTokenResponse> {
  // Body has grant_type, code, redirect_uri, code_verifier. NO client_id /
  // client_secret in the body — those go in the Basic auth header.
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const result = await safeFetch(
    REDDIT_TOKEN_URL,
    {
      method: "POST",
      headers: {
        Authorization: buildBasicAuthHeader(clientId, clientSecret),
        "Content-Type": "application/x-www-form-urlencoded",
        "User-Agent": userAgent,
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new RedditAdsTokenError(
      sanitizeErrorMessage(
        `Reddit Ads token exchange failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as RedditAdsTokenResponse;

  validateTokenResponse(data, /* requireRefreshToken */ true);
  validateScope(data.scope);

  return data;
}

// ============================================================================
// Token Refresh
// ============================================================================

/**
 * Refreshes a Reddit Ads access token via the refresh_token grant.
 *
 * **CRITICAL: Reddit refresh tokens DO NOT rotate.** The refresh response does
 * NOT include a `refresh_token` field (Reddit's docs state "refresh_token will
 * only be present if one was requested" — and it is only requested via
 * `duration=permanent` on the INITIAL `/authorize` call, not on subsequent
 * refreshes).
 *
 * The caller (`redditAdsAdapter.refreshToken`) MUST carry forward the existing
 * `credentials.refreshToken` unchanged on every refresh. This **inverts the
 * Snapchat 19.7 defensive-rotation pattern** (Snap always returns it, Reddit
 * never does). Defensive future-proof: if Reddit ever does start returning a
 * refresh_token field, the adapter persists it.
 *
 * Authentication: HTTP Basic Auth (same as initial exchange — diverges from
 * Snapchat). User-Agent header REQUIRED.
 */
export async function refreshRedditAdsToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string,
  userAgent: string
): Promise<RedditAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  });

  const result = await safeFetch(
    REDDIT_TOKEN_URL,
    {
      method: "POST",
      headers: {
        Authorization: buildBasicAuthHeader(clientId, clientSecret),
        "Content-Type": "application/x-www-form-urlencoded",
        "User-Agent": userAgent,
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new RedditAdsTokenError(
      sanitizeErrorMessage(
        `Reddit Ads token refresh failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as RedditAdsTokenResponse;

  // refresh_token is OPTIONAL on the refresh path — Reddit's documented
  // behaviour is to omit it. The adapter carries forward the existing one.
  validateTokenResponse(data, /* requireRefreshToken */ false);

  return data;
}

// ============================================================================
// Internal: token-response validation
// ============================================================================

/**
 * Validates that a Reddit token response has the fields we require and that
 * they're the correct type. Reddit documents `expires_in` as an integer but a
 * future schema drift (e.g., `"3600"` string) would silently break
 * `Date.now() + expires_in * 1000` — string-multiplication yields NaN. We
 * catch that here and throw a clear error instead.
 */
export function validateTokenResponse(
  data: RedditAdsTokenResponse,
  requireRefreshToken: boolean
): void {
  if (typeof data.access_token !== "string" || data.access_token.length === 0) {
    throw new Error("Invalid Reddit Ads token response: missing access_token");
  }
  if (requireRefreshToken) {
    if (
      typeof data.refresh_token !== "string" ||
      data.refresh_token.length === 0
    ) {
      throw new Error(
        "Invalid Reddit Ads token response: missing refresh_token (did you forget duration=permanent on /authorize?)"
      );
    }
  }
  if (typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in)) {
    throw new Error(
      `Invalid Reddit Ads token response: expires_in must be a finite number (received ${typeof data.expires_in})`
    );
  }
}

/**
 * Validates that the returned scope contains the requested `adsread` scope.
 * Silent scope-narrowing is a known OAuth attack vector; if Reddit returns
 * `""` or omits our scope, fail loud and never store under-scoped tokens
 * (AC #2 invariant).
 *
 * Reddit returns scope as a space-separated string (RFC 6749 §3.3).
 *
 * @internal exported for direct unit testing only
 */
export function validateScope(scope: string | undefined): void {
  if (typeof scope !== "string" || scope.length === 0) {
    throw new RedditAdsTokenError(
      "Reddit Ads scope-narrowed: provider returned empty scope",
      { oauthError: "scope-narrowed" }
    );
  }
  const granted = scope.split(/\s+/).filter(Boolean);
  for (const required of REDDIT_ADS_SCOPES) {
    if (!granted.includes(required)) {
      throw new RedditAdsTokenError(
        sanitizeErrorMessage(
          `Reddit Ads scope-narrowed: required scope "${required}" not granted (received "${scope}")`
        ),
        { oauthError: "scope-narrowed" }
      );
    }
  }
}

// ============================================================================
// Callback Error Parsing
// ============================================================================

/**
 * Parses Reddit OAuth callback errors from query params.
 *
 * Reddit returns standard OAuth 2.0 error codes as `?error=...`. We map them
 * to user-facing messages and a retry hint per the documented Reddit error
 * codes at https://github.com/reddit-archive/reddit/wiki/OAuth2.
 */
export function parseRedditAdsCallbackError(
  searchParams: URLSearchParams
): RedditAdsCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You denied the Reddit Advertising connection request.",
      userAction: "retry",
    };
  }

  if (
    error === "invalid_request" ||
    error === "unauthorized_client" ||
    error === "redirect_uri_mismatch" ||
    error === "unsupported_response_type" ||
    error === "unsupported_grant_type"
  ) {
    return {
      code: "oauth-config-error",
      userMessage:
        "Reddit Advertising connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  if (error === "invalid_scope") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Reddit Advertising connection is misconfigured (invalid scopes). Contact support.",
      userAction: "none",
    };
  }

  if (error === "server_error" || error === "temporarily_unavailable") {
    return {
      code: "oauth-error",
      userMessage:
        "Reddit Advertising is temporarily unavailable. Please try again later.",
      userAction: "retry",
    };
  }

  if (error === "invalid_grant") {
    // In the callback path, invalid_grant means the auth code expired or was
    // replayed. User-actionable: try connecting again.
    return {
      code: "oauth-error",
      userMessage:
        "Your Reddit sign-in session expired. Please try connecting again.",
      userAction: "retry",
    };
  }

  return {
    code: "oauth-error",
    userMessage:
      "An error occurred connecting to Reddit Advertising. Please try again.",
    userAction: "retry",
  };
}
